import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ClockIcon } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
interface BlogPost {
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  featured?: boolean;
}
const featuredPost: BlogPost = {
  slug: 'character-arc-tracking',
  category: 'Craft',
  title: 'The difference between a character arc and a character change — and why it matters for your screenplay.',
  excerpt: 'Most writers know their protagonist needs to change. Fewer understand the difference between a character who changes and a character who has an arc. One is a plot event. The other is the engine of your entire story.',
  author: 'The Slate & Page Team',
  date: 'February 28, 2026',
  readTime: '8 min read',
  featured: true
};
export function BlogPage() {
  return <main className="w-full bg-cream" data-id="element-176">
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 lg:px-8" aria-labelledby="blog-heading" data-id="element-177">
        
        <div className="max-w-7xl mx-auto" data-id="element-178">
          <div className="max-w-2xl" data-id="element-179">
            <Badge variant="amber" className="mb-6" data-id="element-180">
              Blog
            </Badge>
            <h1 id="blog-heading" className="font-serif text-5xl md:text-6xl font-bold text-ink mb-6 leading-tight" data-id="element-181">
              
              Writing, craft, and the tools that help.
            </h1>
            <p className="font-sans text-lg text-ink-muted leading-relaxed" data-id="element-182">
              Thoughts on screenwriting craft, story development, and building
              Slate & Page — from the team and the writers who use it.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="pb-16 px-6 lg:px-8" aria-labelledby="featured-post-heading" data-id="element-183">
        
        <div className="max-w-7xl mx-auto" data-id="element-184">
          <div className="bg-navy rounded-2xl overflow-hidden" data-id="element-185">
            <div className="grid lg:grid-cols-2" data-id="element-186">
              <div className="p-10 lg:p-14 flex flex-col justify-center" data-id="element-187">
                <div className="flex items-center gap-3 mb-5" data-id="element-188">
                  <span className="font-sans text-xs font-medium text-amber uppercase tracking-widest" data-id="element-189">
                    Featured
                  </span>
                  <span className="w-1 h-1 rounded-full bg-white/20" data-id="element-190"></span>
                  <span className="font-sans text-xs text-white/40" data-id="element-191">
                    {featuredPost.category}
                  </span>
                </div>
                <h2 id="featured-post-heading" className="font-serif text-3xl md:text-4xl font-bold text-white mb-5 leading-tight" data-id="element-192">
                  
                  {featuredPost.title}
                </h2>
                <p className="font-sans text-base text-white/60 leading-relaxed mb-8" data-id="element-193">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center justify-between" data-id="element-194">
                  <div className="flex items-center gap-3" data-id="element-195">
                    <div className="w-8 h-8 rounded-full bg-amber/20 flex items-center justify-center" data-id="element-196">
                      <span className="font-serif text-sm font-bold text-amber" data-id="element-197">
                        S
                      </span>
                    </div>
                    <div data-id="element-198">
                      <div className="font-sans text-xs font-medium text-white/80" data-id="element-199">
                        {featuredPost.author}
                      </div>
                      <div className="font-sans text-xs text-white/40" data-id="element-200">
                        {featuredPost.date}
                      </div>
                    </div>
                  </div>
                  <a href="#" className="font-sans text-sm font-medium text-amber hover:text-amber-light flex items-center gap-2 transition-colors" data-id="element-201">
                    
                    Read
                    <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-202" />
                  </a>
                </div>
              </div>
              <div className="bg-navy-light p-10 lg:p-14 flex items-center justify-center" data-id="element-203">
                <div className="bg-white/5 border border-white/10 rounded-xl p-8 max-w-sm" data-id="element-204">
                  <div className="font-serif text-2xl italic text-white/80 leading-relaxed mb-4" data-id="element-205">
                    "A character arc isn't what happens to your protagonist.
                    It's what they become because of what happens."
                  </div>
                  <div className="font-sans text-xs text-white/40" data-id="element-206">
                    From the article
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="pb-8 px-6 lg:px-8" aria-label="Blog categories" data-id="element-207">
        <div className="max-w-7xl mx-auto" data-id="element-208">
          <div className="flex flex-wrap gap-2" data-id="element-209">
            <button className="font-sans text-sm font-medium bg-navy text-white px-4 py-2 rounded-full transition-colors" data-id="element-210">
              All
            </button>
            <button className="font-sans text-sm text-ink-muted hover:text-ink bg-parchment hover:bg-parchment/80 px-4 py-2 rounded-full transition-colors" data-id="element-211">
              Craft
            </button>
            <button className="font-sans text-sm text-ink-muted hover:text-ink bg-parchment hover:bg-parchment/80 px-4 py-2 rounded-full transition-colors" data-id="element-212">
              Product
            </button>
            <button className="font-sans text-sm text-ink-muted hover:text-ink bg-parchment hover:bg-parchment/80 px-4 py-2 rounded-full transition-colors" data-id="element-213">
              Workflow
            </button>
            <button className="font-sans text-sm text-ink-muted hover:text-ink bg-parchment hover:bg-parchment/80 px-4 py-2 rounded-full transition-colors" data-id="element-214">
              World-Building
            </button>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="pb-24 px-6 lg:px-8" aria-label="Blog posts" data-id="element-215">
        <div className="max-w-7xl mx-auto" data-id="element-216">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-217">
            {/* Post 1 */}
            <article className="bg-white rounded-2xl border border-parchment shadow-sm hover:shadow-md transition-shadow overflow-hidden group" data-id="element-218">
              <div className="bg-parchment h-48 flex items-center justify-center p-8" data-id="element-219">
                <div className="text-center" data-id="element-220">
                  <div className="font-serif text-4xl font-bold text-amber/40 mb-2" data-id="element-221">
                    01
                  </div>
                  <div className="font-sans text-xs text-ink-light uppercase tracking-widest" data-id="element-222">
                    Craft
                  </div>
                </div>
              </div>
              <div className="p-6" data-id="element-223">
                <div className="flex items-center gap-2 mb-3" data-id="element-224">
                  <span className="font-sans text-xs font-medium text-amber uppercase tracking-wide" data-id="element-225">
                    Craft
                  </span>
                  <span className="w-1 h-1 rounded-full bg-ink-light" data-id="element-226"></span>
                  <span className="font-sans text-xs text-ink-light flex items-center gap-1" data-id="element-227">
                    <ClockIcon className="w-3 h-3" aria-hidden="true" data-id="element-228" />6 min
                    read
                  </span>
                </div>
                <h3 className="font-serif text-xl font-semibold text-ink mb-3 leading-tight group-hover:text-amber transition-colors" data-id="element-229">
                  How to write a scene that does three things at once.
                </h3>
                <p className="font-sans text-sm text-ink-muted leading-relaxed mb-5" data-id="element-230">
                  The best scenes in any screenplay are doing multiple jobs
                  simultaneously: advancing plot, revealing character, and
                  building world. Here's how to think about it.
                </p>
                <div className="flex items-center justify-between" data-id="element-231">
                  <div className="font-sans text-xs text-ink-light" data-id="element-232">
                    Feb 14, 2026
                  </div>
                  <a href="#" className="font-sans text-xs font-medium text-amber hover:text-amber-dark flex items-center gap-1 transition-colors" data-id="element-233">
                    
                    Read{' '}
                    <ArrowRightIcon className="w-3 h-3" aria-hidden="true" data-id="element-234" />
                  </a>
                </div>
              </div>
            </article>

            {/* Post 2 */}
            <article className="bg-white rounded-2xl border border-parchment shadow-sm hover:shadow-md transition-shadow overflow-hidden group" data-id="element-235">
              <div className="bg-navy h-48 flex items-center justify-center p-8" data-id="element-236">
                <div className="text-center" data-id="element-237">
                  <div className="font-serif text-4xl font-bold text-amber/40 mb-2" data-id="element-238">
                    02
                  </div>
                  <div className="font-sans text-xs text-white/30 uppercase tracking-widest" data-id="element-239">
                    Product
                  </div>
                </div>
              </div>
              <div className="p-6" data-id="element-240">
                <div className="flex items-center gap-2 mb-3" data-id="element-241">
                  <span className="font-sans text-xs font-medium text-amber uppercase tracking-wide" data-id="element-242">
                    Product
                  </span>
                  <span className="w-1 h-1 rounded-full bg-ink-light" data-id="element-243"></span>
                  <span className="font-sans text-xs text-ink-light flex items-center gap-1" data-id="element-244">
                    <ClockIcon className="w-3 h-3" aria-hidden="true" data-id="element-245" />4 min
                    read
                  </span>
                </div>
                <h3 className="font-serif text-xl font-semibold text-ink mb-3 leading-tight group-hover:text-amber transition-colors" data-id="element-246">
                  Introducing world-building: the new module for building your
                  story's universe.
                </h3>
                <p className="font-sans text-sm text-ink-muted leading-relaxed mb-5" data-id="element-247">
                  Today we're launching the world-building module — a structured
                  system for rules, objects, factions, terminology, and
                  historical events. Here's what it does and why we built it.
                </p>
                <div className="flex items-center justify-between" data-id="element-248">
                  <div className="font-sans text-xs text-ink-light" data-id="element-249">
                    Feb 1, 2026
                  </div>
                  <a href="#" className="font-sans text-xs font-medium text-amber hover:text-amber-dark flex items-center gap-1 transition-colors" data-id="element-250">
                    
                    Read{' '}
                    <ArrowRightIcon className="w-3 h-3" aria-hidden="true" data-id="element-251" />
                  </a>
                </div>
              </div>
            </article>

            {/* Post 3 */}
            <article className="bg-white rounded-2xl border border-parchment shadow-sm hover:shadow-md transition-shadow overflow-hidden group" data-id="element-252">
              <div className="bg-amber-light h-48 flex items-center justify-center p-8" data-id="element-253">
                <div className="text-center" data-id="element-254">
                  <div className="font-serif text-4xl font-bold text-amber/60 mb-2" data-id="element-255">
                    03
                  </div>
                  <div className="font-sans text-xs text-warm-brown/60 uppercase tracking-widest" data-id="element-256">
                    Workflow
                  </div>
                </div>
              </div>
              <div className="p-6" data-id="element-257">
                <div className="flex items-center gap-2 mb-3" data-id="element-258">
                  <span className="font-sans text-xs font-medium text-amber uppercase tracking-wide" data-id="element-259">
                    Workflow
                  </span>
                  <span className="w-1 h-1 rounded-full bg-ink-light" data-id="element-260"></span>
                  <span className="font-sans text-xs text-ink-light flex items-center gap-1" data-id="element-261">
                    <ClockIcon className="w-3 h-3" aria-hidden="true" data-id="element-262" />
                    10 min read
                  </span>
                </div>
                <h3 className="font-serif text-xl font-semibold text-ink mb-3 leading-tight group-hover:text-amber transition-colors" data-id="element-263">
                  My development workflow: from idea to locked outline in six
                  weeks.
                </h3>
                <p className="font-sans text-sm text-ink-muted leading-relaxed mb-5" data-id="element-264">
                  A TV writer shares how she uses Slate & Page alongside Final
                  Draft to develop a pilot from a one-line premise to a
                  production-ready outline.
                </p>
                <div className="flex items-center justify-between" data-id="element-265">
                  <div className="font-sans text-xs text-ink-light" data-id="element-266">
                    Jan 22, 2026
                  </div>
                  <a href="#" className="font-sans text-xs font-medium text-amber hover:text-amber-dark flex items-center gap-1 transition-colors" data-id="element-267">
                    
                    Read{' '}
                    <ArrowRightIcon className="w-3 h-3" aria-hidden="true" data-id="element-268" />
                  </a>
                </div>
              </div>
            </article>

            {/* Post 4 */}
            <article className="bg-white rounded-2xl border border-parchment shadow-sm hover:shadow-md transition-shadow overflow-hidden group" data-id="element-269">
              <div className="bg-parchment h-48 flex items-center justify-center p-8" data-id="element-270">
                <div className="text-center" data-id="element-271">
                  <div className="font-serif text-4xl font-bold text-amber/40 mb-2" data-id="element-272">
                    04
                  </div>
                  <div className="font-sans text-xs text-ink-light uppercase tracking-widest" data-id="element-273">
                    World-Building
                  </div>
                </div>
              </div>
              <div className="p-6" data-id="element-274">
                <div className="flex items-center gap-2 mb-3" data-id="element-275">
                  <span className="font-sans text-xs font-medium text-amber uppercase tracking-wide" data-id="element-276">
                    World-Building
                  </span>
                  <span className="w-1 h-1 rounded-full bg-ink-light" data-id="element-277"></span>
                  <span className="font-sans text-xs text-ink-light flex items-center gap-1" data-id="element-278">
                    <ClockIcon className="w-3 h-3" aria-hidden="true" data-id="element-279" />7 min
                    read
                  </span>
                </div>
                <h3 className="font-serif text-xl font-semibold text-ink mb-3 leading-tight group-hover:text-amber transition-colors" data-id="element-280">
                  The iceberg principle: how much of your world should the
                  audience see?
                </h3>
                <p className="font-sans text-sm text-ink-muted leading-relaxed mb-5" data-id="element-281">
                  The most immersive story worlds feel like they extend far
                  beyond what's on screen. Here's how to build the 90% that
                  never appears in your script — and why it matters.
                </p>
                <div className="flex items-center justify-between" data-id="element-282">
                  <div className="font-sans text-xs text-ink-light" data-id="element-283">
                    Jan 10, 2026
                  </div>
                  <a href="#" className="font-sans text-xs font-medium text-amber hover:text-amber-dark flex items-center gap-1 transition-colors" data-id="element-284">
                    
                    Read{' '}
                    <ArrowRightIcon className="w-3 h-3" aria-hidden="true" data-id="element-285" />
                  </a>
                </div>
              </div>
            </article>

            {/* Post 5 */}
            <article className="bg-white rounded-2xl border border-parchment shadow-sm hover:shadow-md transition-shadow overflow-hidden group" data-id="element-286">
              <div className="bg-navy h-48 flex items-center justify-center p-8" data-id="element-287">
                <div className="text-center" data-id="element-288">
                  <div className="font-serif text-4xl font-bold text-amber/40 mb-2" data-id="element-289">
                    05
                  </div>
                  <div className="font-sans text-xs text-white/30 uppercase tracking-widest" data-id="element-290">
                    Craft
                  </div>
                </div>
              </div>
              <div className="p-6" data-id="element-291">
                <div className="flex items-center gap-2 mb-3" data-id="element-292">
                  <span className="font-sans text-xs font-medium text-amber uppercase tracking-wide" data-id="element-293">
                    Craft
                  </span>
                  <span className="w-1 h-1 rounded-full bg-ink-light" data-id="element-294"></span>
                  <span className="font-sans text-xs text-ink-light flex items-center gap-1" data-id="element-295">
                    <ClockIcon className="w-3 h-3" aria-hidden="true" data-id="element-296" />5 min
                    read
                  </span>
                </div>
                <h3 className="font-serif text-xl font-semibold text-ink mb-3 leading-tight group-hover:text-amber transition-colors" data-id="element-297">
                  Location as character: how your settings can carry emotional
                  weight.
                </h3>
                <p className="font-sans text-sm text-ink-muted leading-relaxed mb-5" data-id="element-298">
                  The best locations in film aren't just backdrops — they're
                  active participants in the story. How to think about your
                  spaces as narrative elements with their own arcs.
                </p>
                <div className="flex items-center justify-between" data-id="element-299">
                  <div className="font-sans text-xs text-ink-light" data-id="element-300">
                    Dec 18, 2025
                  </div>
                  <a href="#" className="font-sans text-xs font-medium text-amber hover:text-amber-dark flex items-center gap-1 transition-colors" data-id="element-301">
                    
                    Read{' '}
                    <ArrowRightIcon className="w-3 h-3" aria-hidden="true" data-id="element-302" />
                  </a>
                </div>
              </div>
            </article>

            {/* Post 6 */}
            <article className="bg-white rounded-2xl border border-parchment shadow-sm hover:shadow-md transition-shadow overflow-hidden group" data-id="element-303">
              <div className="bg-amber-light h-48 flex items-center justify-center p-8" data-id="element-304">
                <div className="text-center" data-id="element-305">
                  <div className="font-serif text-4xl font-bold text-amber/60 mb-2" data-id="element-306">
                    06
                  </div>
                  <div className="font-sans text-xs text-warm-brown/60 uppercase tracking-widest" data-id="element-307">
                    Product
                  </div>
                </div>
              </div>
              <div className="p-6" data-id="element-308">
                <div className="flex items-center gap-2 mb-3" data-id="element-309">
                  <span className="font-sans text-xs font-medium text-amber uppercase tracking-wide" data-id="element-310">
                    Product
                  </span>
                  <span className="w-1 h-1 rounded-full bg-ink-light" data-id="element-311"></span>
                  <span className="font-sans text-xs text-ink-light flex items-center gap-1" data-id="element-312">
                    <ClockIcon className="w-3 h-3" aria-hidden="true" data-id="element-313" />3 min
                    read
                  </span>
                </div>
                <h3 className="font-serif text-xl font-semibold text-ink mb-3 leading-tight group-hover:text-amber transition-colors" data-id="element-314">
                  Why we're building Slate & Page in public.
                </h3>
                <p className="font-sans text-sm text-ink-muted leading-relaxed mb-5" data-id="element-315">
                  We're sharing our roadmap, our decisions, and our reasoning
                  openly. Here's why transparency matters to us — and what it
                  means for you as an early user.
                </p>
                <div className="flex items-center justify-between" data-id="element-316">
                  <div className="font-sans text-xs text-ink-light" data-id="element-317">
                    Dec 5, 2025
                  </div>
                  <a href="#" className="font-sans text-xs font-medium text-amber hover:text-amber-dark flex items-center gap-1 transition-colors" data-id="element-318">
                    
                    Read{' '}
                    <ArrowRightIcon className="w-3 h-3" aria-hidden="true" data-id="element-319" />
                  </a>
                </div>
              </div>
            </article>
          </div>

          {/* Load More */}
          <div className="mt-12 text-center" data-id="element-320">
            <button className="font-sans text-sm font-medium text-ink-muted hover:text-ink border border-parchment hover:border-ink-light px-8 py-3 rounded-full transition-colors" data-id="element-321">
              Load more articles
            </button>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="bg-parchment py-24 px-6 lg:px-8" aria-labelledby="newsletter-heading" data-id="element-322">
        
        <div className="max-w-2xl mx-auto text-center" data-id="element-323">
          <h2 id="newsletter-heading" className="font-serif text-3xl font-bold text-ink mb-4" data-id="element-324">
            
            Get new articles in your inbox.
          </h2>
          <p className="font-sans text-base text-ink-muted mb-8" data-id="element-325">
            Craft essays, product updates, and writing guides — a few times a
            month, never more.
          </p>
          <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" onSubmit={e => e.preventDefault()} aria-label="Newsletter signup" data-id="element-326">
            
            <input type="email" placeholder="your@email.com" className="flex-1 font-sans text-sm bg-white border border-parchment rounded-full px-5 py-3 text-ink placeholder:text-ink-light focus:outline-none focus:ring-2 focus:ring-amber/30 focus:border-amber transition-colors" aria-label="Email address" data-id="element-327" />
            
            <button type="submit" className="font-sans text-sm font-medium bg-amber hover:bg-amber-dark text-white px-6 py-3 rounded-full transition-colors flex-shrink-0" data-id="element-328">
              
              Subscribe
            </button>
          </form>
          <p className="font-sans text-xs text-ink-light mt-4" data-id="element-329">
            No spam. Unsubscribe anytime.
          </p>
        </div>
      </section>
    </main>;
}